import type { ParcelPropertiesInfo } from "../types";

type ParcelSortKey = "favourite" | "id" | "numer" | "pog_pum" | "mpzp_pum";
type ParcelSortDirection = "asc" | "desc";
type ParcelSort = { key: ParcelSortKey; direction: ParcelSortDirection };

// numeric: true — otherwise "12" sorts after "100" in a plain lexical comparison.
const PARCEL_COLLATOR = new Intl.Collator("pl", { numeric: true });

// The PUM fields are strings and missing data is "" (the table renders "-" for it). Number("") is 0, so an
// empty value has to be screened out explicitly before it reaches the comparison.
function parsePum(value: string): number | null {
	const parsed = Number(value);
	return value !== "" && Number.isFinite(parsed) === true ? parsed : null;
}

// favouriteIds is passed in because "favourite" isn't a parcel field — it derives from selectedParcels.
function compareParcelRows(a: ParcelPropertiesInfo, b: ParcelPropertiesInfo, sort: ParcelSort, favouriteIds: ReadonlySet<string>): number {
	const factor = sort.direction === "asc" ? 1 : -1;

	if (sort.key === "favourite") {
		const aIsFavourite = favouriteIds.has(a.id);
		const bIsFavourite = favouriteIds.has(b.id);

		// "asc" puts favourites on top — the first click on the star should pull them up, not bury them.
		if (aIsFavourite !== bIsFavourite) {
			return (aIsFavourite === true ? -1 : 1) * factor;
		}

		// Order within a group is arbitrary source order, so fall back to a deterministic tiebreak.
		return PARCEL_COLLATOR.compare(a.id, b.id);
	}

	if (sort.key === "pog_pum" || sort.key === "mpzp_pum") {
		const aValue = parsePum(a[sort.key]);
		const bValue = parsePum(b[sort.key]);

		// Empties go last regardless of direction — otherwise "descending" floats a block of dashes to the top.
		if (aValue === null || bValue === null) {
			return (aValue === null ? 1 : 0) - (bValue === null ? 1 : 0);
		}

		return (aValue - bValue) * factor;
	}

	return PARCEL_COLLATOR.compare(a[sort.key], b[sort.key]) * factor;
}

export { compareParcelRows, type ParcelSort, type ParcelSortDirection, type ParcelSortKey };
